// Ported from dev-scrips-pm-react src/billing/services/use-provider-fees-csv.ts (PROD-1210 Provider Fees data layer).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, postForm } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import type {
  ExportCsvResponse,
  ProviderServiceFee,
  ProviderServiceFeesImportCsvRequest,
  ValidateCsvResponse,
} from '@/shared/types/billing';

/**
 * Provider Service Fees CSV operations.
 *
 * Mirrors Flutter `billing_data_source_impl.dart:709-896`:
 *
 *   GET  /ProviderServiceFees/DownloadCsvTemplate         — returns a quoted URL string
 *   POST /ProviderServiceFees/ExportCsv/{providerId}      — body { providerServiceFeeIds }, returns { csvLink }
 *   POST /ProviderServiceFees/ValidateCsv/{providerId}    — multipart, returns ValidateCsvResponse
 *   POST /ProviderServiceFees/ImportCsv/{providerId}      — body { requestId, fileName, removeExisting }, returns ProviderServiceFee[]
 *
 * 2-stage import flow:
 *   1. Validate → server returns { requestId, validRecords, invalidRecords, invalidRecordsErrors }
 *   2. Import   → call with the requestId to commit; server returns the imported rows.
 *
 * Unlike service-list CSV, every endpoint (except the blank template) is
 * scoped to a single provider. The multipart validate uses PA's `postForm`
 * helper (browser sets the multipart boundary).
 */

interface ValidateCsvParams {
  providerId: string;
  csvFile: File;
}

export function useValidateProviderFeesCsv() {
  return useMutation({
    mutationFn: ({ providerId, csvFile }: ValidateCsvParams) => {
      const fd = new FormData();
      fd.append('CsvFile', csvFile, csvFile.name);
      return postForm<ValidateCsvResponse>(
        `${BILLING_BASE}/ProviderServiceFees/ValidateCsv/${providerId}`,
        fd,
      );
    },
  });
}

interface ImportCsvParams extends ProviderServiceFeesImportCsvRequest {
  providerId: string;
}

export function useImportProviderFeesCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ providerId, requestId, fileName, removeExisting }: ImportCsvParams) =>
      post<ProviderServiceFee[]>(
        `${BILLING_BASE}/ProviderServiceFees/ImportCsv/${providerId}`,
        {
          requestId,
          fileName,
          removeExisting: Boolean(removeExisting),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing', 'provider-fees'] });
    },
  });
}

/**
 * Fetch the blank CSV template URL. Server returns a JSON-encoded string
 * (`"https://..."`); the JSON parser unwraps the quotes for us.
 */
export function useDownloadProviderFeesCsvTemplate() {
  return useMutation({
    mutationFn: () =>
      get<string>(`${BILLING_BASE}/ProviderServiceFees/DownloadCsvTemplate`),
  });
}

interface ExportParams {
  providerId: string;
  /** Optional subset of fee ids to export. Empty / omitted exports all. */
  providerServiceFeeIds?: string[];
}

/**
 * Per-provider fee export. Server returns `{ csvLink }` — open the link
 * in a new tab or set `window.location = csvLink` to trigger download.
 */
export function useExportProviderFeesCsv() {
  return useMutation({
    mutationFn: ({ providerId, providerServiceFeeIds = [] }: ExportParams) =>
      post<ExportCsvResponse>(
        `${BILLING_BASE}/ProviderServiceFees/ExportCsv/${providerId}`,
        { providerServiceFeeIds },
      ),
  });
}
